/**
 * Componente Sidebar
 *
 * Menu lateral de navegação estilo ERP.
 *
 * Funcionalidades:
 * - Expansível/Colapsável (280px/80px)
 * - Navegação organizada por grupos
 * - Indicador de item ativo
 * - Logo do estabelecimento
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  ShoppingBag,
  Package,
  FolderTree,
  Users,
  UtensilsCrossed,
  Truck,
  Wallet,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  MessageCircle,
  Shield,
  Boxes,
  SlidersHorizontal,
  Receipt,
  Megaphone,
  Store,
  FileSearch,
  UserCog,
  Bell,
  Building2,
  Image,
  Crown,
  MonitorPlay,
  CalendarClock,
  Briefcase,
  GitBranch,
  Target,
  ArrowDownCircle,
  ArrowUpCircle,
  Landmark,
  ArrowLeftRight,
  FileSpreadsheet,
  PieChart,
  UserPlus,
  ShoppingCart,
  Gem,
  Lock,
  CircleDashed,
  MessagesSquare,
  Bike,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { useInboxUnread } from '@hooks/useInboxUnread';
import { usePlanAccess, PlanModuleKey } from '@hooks/usePlanAccess';
import { api } from '@services/api';
import { USER_MANUAL_HINT, USER_MANUAL_URL } from '@utils/userManual';
import {
  iconWhite,
  iconPrimary,
  logoHorizontalDark,
  logoHorizontalLight,
} from '@assets/index';

/**
 * Props do componente Sidebar
 */
interface SidebarProps {
  /** Se a sidebar está colapsada */
  collapsed: boolean;
  /** Callback para alternar estado */
  onToggle: () => void;
  /** Chamado ao abrir um link fora do app (ex.: manual em nova aba); no celular fecha o menu */
  onLeave?: () => void;
}

/**
 * Item de navegação
 */
interface NavItem {
  /** Label do item */
  label: string;
  /** Caminho da rota */
  path: string;
  /** Ícone do item */
  icon: React.ReactNode;
  /** Chave de feature flag nas settings.business do tenant (ex: 'schedulingEnabled') */
  featureKey?: string;
  /** Módulo do plano exigido (exibe cadeado quando não contratado) */
  planModule?: PlanModuleKey;
  /** Papéis que veem o item (se undefined, todos os que veem o grupo) */
  requiredRole?: string[];
  /** Contador exibido ao lado do item (ex.: conversas não lidas) */
  badgeKey?: 'inboxUnread';
}

/**
 * Grupo de navegação
 */
interface NavGroup {
  /** Label do grupo */
  label: string;
  /** Itens do grupo */
  items: NavItem[];
  /** Role(s) necessária(s) para exibir o grupo (se undefined, exibe para todos) */
  requiredRole?: string | string[];
}

/**
 * Grupos de navegação da sidebar
 */
const navGroups: NavGroup[] = [
  {
    label: 'Principal',
    items: [
      {
        label: 'Painel',
        path: '/painel',
        icon: <LayoutDashboard size={20} />,
      },
      {
        label: 'Pedidos',
        path: '/pedidos',
        icon: <ShoppingBag size={20} />,
      },
      {
        label: 'Fila de Pedidos',
        path: '/fila-pedidos',
        icon: <MonitorPlay size={20} />,
      },
    ],
  },
  {
    label: 'Catálogo',
    items: [
      {
        label: 'Produtos',
        path: '/produtos',
        icon: <Package size={20} />,
      },
      {
        label: 'Categorias',
        path: '/categorias',
        icon: <FolderTree size={20} />,
      },
      {
        label: 'Personalizacao',
        path: '/personalizacao',
        icon: <SlidersHorizontal size={20} />,
      },
    ],
  },
  {
    label: 'Operação',
    items: [
      {
        label: 'Agendamentos',
        path: '/agendamentos',
        icon: <CalendarClock size={20} />,
        featureKey: 'schedulingEnabled',
      },
      {
        label: 'Clientes',
        path: '/clientes',
        icon: <Users size={20} />,
      },
      {
        label: 'Mesas',
        path: '/mesas',
        planModule: 'tables',
        icon: <UtensilsCrossed size={20} />,
      },
      {
        label: 'Entregas',
        path: '/entregas',
        icon: <Truck size={20} />,
      },
      {
        label: 'Entregas por aplicativo',
        path: '/entregas-app',
        icon: <Bike size={20} />,
        requiredRole: ['SUPER_ADMIN', 'OWNER', 'MANAGER'],
      },
      {
        label: 'Pagamentos',
        path: '/pagamentos',
        icon: <CreditCard size={20} />,
      },
      {
        label: 'Estoque',
        path: '/estoque',
        planModule: 'inventory',
        icon: <Boxes size={20} />,
      },
    ],
  },
  {
    label: 'Integração',
    items: [
      {
        label: 'Atendimento',
        path: '/atendimento',
        planModule: 'whatsapp',
        requiredRole: ['SUPER_ADMIN', 'OWNER', 'MANAGER', 'ATTENDANT'],
        badgeKey: 'inboxUnread',
        icon: <MessagesSquare size={20} />,
      },
      {
        label: 'WhatsApp',
        path: '/whatsapp',
        planModule: 'whatsapp',
        requiredRole: ['SUPER_ADMIN', 'OWNER', 'MANAGER', 'ATTENDANT'],
        icon: <MessageCircle size={20} />,
      },
      {
        label: 'Status do WhatsApp',
        path: '/status-whatsapp',
        planModule: 'whatsapp',
        requiredRole: ['SUPER_ADMIN', 'OWNER', 'MANAGER'],
        icon: <CircleDashed size={20} />,
      },
    ],
  },
  {
    label: 'Financeiro',
    // Pendência 034: Financeiro só para Proprietário e Gerente
    requiredRole: ['SUPER_ADMIN', 'OWNER', 'MANAGER'],
    items: [
      {
        label: 'Financeiro',
        path: '/financeiro',
        planModule: 'financial',
        icon: <Wallet size={20} />,
      },
      {
        label: 'Despesas',
        path: '/despesas',
        planModule: 'financial',
        icon: <ArrowDownCircle size={20} />,
      },
      {
        label: 'Receitas',
        path: '/receitas',
        planModule: 'financial',
        icon: <ArrowUpCircle size={20} />,
      },
      {
        label: 'Contas Bancárias',
        path: '/contas-bancarias',
        planModule: 'financial',
        icon: <Landmark size={20} />,
      },
      {
        label: 'Transferências',
        path: '/transferencias',
        planModule: 'financial',
        icon: <ArrowLeftRight size={20} />,
      },
      {
        label: 'Conciliação',
        path: '/conciliacao-bancaria',
        planModule: 'bankReconciliation',
        icon: <FileSpreadsheet size={20} />,
      },
      {
        label: 'Fornecedores',
        path: '/fornecedores',
        planModule: 'inventory',
        icon: <Building2 size={20} />,
      },
      {
        label: 'Plano de Custos',
        path: '/plano-custos',
        planModule: 'financial',
        icon: <GitBranch size={20} />,
      },
      {
        label: 'Unid. de Custos',
        path: '/unidade-custos',
        planModule: 'financial',
        icon: <Target size={20} />,
      },
      {
        label: 'Rel. Financeiros',
        path: '/relatorios-financeiros',
        planModule: 'financial',
        icon: <PieChart size={20} />,
      },
      {
        label: 'Fiscal',
        path: '/fiscal',
        planModule: 'fiscal',
        icon: <Receipt size={20} />,
      },
    ],
  },
  {
    label: 'Gestão',
    items: [
      {
        label: 'Relatórios',
        path: '/relatorios',
        icon: <BarChart3 size={20} />,
      },
      {
        label: 'Marketing',
        path: '/marketing',
        icon: <Megaphone size={20} />,
      },
      {
        label: 'Leads',
        path: '/leads',
        planModule: 'whatsapp',
        requiredRole: ['SUPER_ADMIN', 'OWNER', 'MANAGER', 'ATTENDANT'],
        icon: <UserPlus size={20} />,
      },
      {
        label: 'Carrinho Aband.',
        path: '/carrinhos-abandonados',
        planModule: 'abandonedCarts',
        requiredRole: ['SUPER_ADMIN', 'OWNER', 'MANAGER', 'ATTENDANT'],
        icon: <ShoppingCart size={20} />,
      },
      {
        label: 'Vitrine',
        path: '/vitrine',
        icon: <Store size={20} />,
      },
      {
        label: 'Configurações',
        path: '/configuracoes',
        icon: <Settings size={20} />,
      },
    ],
  },
  {
    label: 'Recursos Humanos',
    requiredRole: ['OWNER', 'MANAGER'],
    items: [
      {
        label: 'Gestão de RH',
        path: '/rh',
        planModule: 'hr',
        icon: <Briefcase size={20} />,
      },
    ],
  },
  {
    label: 'Administração',
    items: [
      {
        label: 'Usuários',
        path: '/usuarios',
        icon: <UserCog size={20} />,
      },
      {
        label: 'Estabelecimento',
        path: '/estabelecimento',
        icon: <Building2 size={20} />,
      },
      {
        label: 'Notificações',
        path: '/notificacoes',
        icon: <Bell size={20} />,
      },
      {
        label: 'Mídias',
        path: '/midias',
        icon: <Image size={20} />,
      },
      {
        label: 'Auditoria',
        path: '/auditoria',
        icon: <FileSearch size={20} />,
      },
    ],
  },
  {
    label: 'Privacidade',
    items: [
      {
        label: 'LGPD',
        path: '/lgpd',
        icon: <Shield size={20} />,
      },
    ],
  },
  {
    label: 'Minha Conta',
    requiredRole: ['OWNER', 'MANAGER'],
    items: [
      {
        label: 'Assinatura',
        path: '/assinatura',
        icon: <Gem size={20} />,
      },
    ],
  },
  {
    label: 'Super Admin',
    requiredRole: 'SUPER_ADMIN',
    items: [
      {
        label: 'Painel Admin',
        path: '/admin',
        icon: <Crown size={20} />,
      },
    ],
  },
];

/**
 * Componente Sidebar
 */
export function Sidebar({ collapsed, onToggle, onLeave }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { hasModule } = usePlanAccess();
  const [enabledFeatures, setEnabledFeatures] = useState<Record<string, boolean>>({});
  // Conversas não lidas da Central de atendimento (#252)
  const inboxUnread = useInboxUnread(
    ['SUPER_ADMIN', 'OWNER', 'MANAGER', 'ATTENDANT'].includes(user?.role ?? '') && hasModule('whatsapp'),
  );
  const badges: Record<NonNullable<NavItem['badgeKey']>, number> = { inboxUnread };

  // Busca settings do tenant para filtrar itens por feature flag
  useEffect(() => {
    api.get('/tenants/me').then(res => {
      const business = res.data?.data?.settings?.business;
      if (business) {
        setEnabledFeatures({
          schedulingEnabled: !!business.schedulingEnabled,
        });
      }
    }).catch(() => {});
  }, []);

  // Filtra grupos baseado na role do usuario e feature flags
  const visibleGroups = navGroups
    .filter(group => !group.requiredRole || (Array.isArray(group.requiredRole) ? group.requiredRole.includes(user?.role ?? '') : group.requiredRole === user?.role))
    .map(group => ({
      ...group,
      items: group.items.filter(item =>
        (!item.featureKey || enabledFeatures[item.featureKey]) &&
        (!item.requiredRole || item.requiredRole.includes(user?.role ?? '')),
      ),
    }))
    .filter(group => group.items.length > 0);

  return (
    <aside
      className={clsx(
        'flex h-full flex-col border-r border-gray-200 bg-white transition-all duration-300 dark:border-gray-700 dark:bg-gray-800',
        collapsed ? 'w-20' : 'w-[280px]',
      )}
    >
      {/* Logo e nome do estabelecimento */}
      <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4 dark:border-gray-700">
        {!collapsed && (
          <div className="flex items-center gap-3">
            {/* Logo horizontal - tema claro */}
            <img
              src={logoHorizontalLight}
              alt="VendeAi"
              className="h-10 w-auto object-contain dark:hidden"
            />
            {/* Logo horizontal - tema escuro (branca) */}
            <img
              src={logoHorizontalDark}
              alt="VendeAi"
              className="hidden h-10 w-auto object-contain dark:block"
            />
          </div>
        )}

        {collapsed && (
          <div className="mx-auto">
            {/* Ícone - tema claro */}
            <img
              src={iconPrimary}
              alt="VendeAi"
              className="h-10 w-10 rounded-lg object-contain dark:hidden"
            />
            {/* Ícone - tema escuro (branco) */}
            <img
              src={iconWhite}
              alt="VendeAi"
              className="hidden h-10 w-10 rounded-lg object-contain dark:block"
            />
          </div>
        )}
      </div>

      {/* Navegação */}
      <nav className="flex-1 overflow-y-auto p-4">
        {visibleGroups.map(group => (
          <div key={group.label} className="mb-6">
            {/* Label do grupo (oculto quando colapsado) */}
            {!collapsed && (
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {group.label}
              </p>
            )}

            {/* Itens do grupo */}
            <ul className="space-y-1">
              {group.items.map(item => {
                // Prefixo de rota inteiro: /entregas não acende em /entregas-app
                const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={clsx(
                        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-100 text-primary-800 dark:bg-secondary-900/50 dark:text-secondary-400'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
                        collapsed && 'justify-center',
                      )}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="relative">
                        {item.icon}
                        {collapsed && item.badgeKey && badges[item.badgeKey] > 0 && (
                          <span className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-error-600 ring-2 ring-white dark:ring-gray-800" aria-hidden="true" />
                        )}
                      </span>
                      {!collapsed && <span className="flex-1">{item.label}</span>}
                      {!collapsed && item.badgeKey && badges[item.badgeKey] > 0 && (
                        <span
                          className="min-w-[1.25rem] rounded-full bg-error-600 px-1.5 py-0.5 text-center text-xs font-semibold text-white"
                          aria-label={`${badges[item.badgeKey]} conversa(s) não lida(s)`}
                        >
                          {badges[item.badgeKey] > 99 ? '99+' : badges[item.badgeKey]}
                        </span>
                      )}
                      {!collapsed && item.planModule && !hasModule(item.planModule) && (
                        <Lock size={14} className="text-gray-500 dark:text-gray-400" aria-label="Não incluído no plano" />
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Manual do usuário (#295) e botão de colapsar */}
      <div className="space-y-1 border-t border-gray-200 p-4 dark:border-gray-700">
        {USER_MANUAL_URL && (
          <a
            href={USER_MANUAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onLeave?.()}
            className={clsx(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
              collapsed && 'justify-center',
            )}
            title={`Manual do usuário (${USER_MANUAL_HINT})`}
            aria-label={`Manual do usuário (${USER_MANUAL_HINT}, abre em nova aba)`}
          >
            <BookOpen size={20} />
            {!collapsed && (
              <span className="flex flex-col leading-tight">
                <span>Manual do usuário</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{USER_MANUAL_HINT}</span>
              </span>
            )}
          </a>
        )}
        <button
          onClick={onToggle}
          className={clsx(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
            collapsed && 'justify-center',
          )}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          {!collapsed && <span>Recolher menu</span>}
        </button>
      </div>
    </aside>
  );
}
